import { useState } from 'react';
import { useIntl } from 'react-intl';
import { Checkbox, RadioButtonsGroup, SelectionableSection } from 'sonata-react-components';
import { checkUserResourcePermissions } from 'sonata-static-storage/lib/helpers/user.helpers';
import { DevicePlatformEnum } from 'sonata-static-storage/lib/schemas/campaign/targeting/devicePlatform.schema';

import { DistributionSlider } from '@/components/common/DistributionSlider/DistributionSlider';
import { useGlobalStore } from '@/store/global';
import { getUser } from '@/store/global/user/selectors';
import { getUserRoles } from '@/store/global/userRoles/selectors';
import { AdvertisingPlatformStyled, PlatformDistributionStyled } from './AdvertisingPlatform.styles';

import type { DevicePlatformType } from 'sonata-static-storage/lib/schemas/targeting/DevicePlatform/DevicePlatform.types';

type PlatformTypes = 'mobile' | 'desktop' | 'hybrid';
type PlatformDistributionType = 'all' | 'specific';

type Props = {
  budget: number;
  devicePlatform: DevicePlatformType[];
  onDevicePlatformChange: (devicePlatform: DevicePlatformType[], unknownIncluded: boolean) => void;
  unknownIncluded: boolean;
};

const calculateDevicePlatform = (
  platform: string,
  distributionType: 'all' | 'specific',
  distribution: number
): DevicePlatformType[] => {
  const isMobileEnabled = ['mobile', 'hybrid'].includes(platform ?? '');
  const isDesktopEnabled = ['desktop', 'hybrid'].includes(platform ?? '');
  const isCtvEnabled = platform === 'ctv';

  let mobileDistribution = 0;
  let desktopDistribution = 0;

  if (platform === 'hybrid') {
    if (distributionType === 'specific') {
      mobileDistribution = 100 - distribution;
      desktopDistribution = distribution;
    } else {
      mobileDistribution = 0;
      desktopDistribution = 0;
    }
  }

  return [
    {
      distribution: mobileDistribution,
      enable: isMobileEnabled,
      name: 'mobile'
    },
    {
      distribution: desktopDistribution,
      enable: isDesktopEnabled,
      name: 'desktop'
    },
    {
      distribution: 0,
      enable: isCtvEnabled,
      name: 'ctv'
    }
  ];
};

export function AdvertisingPlatform({ budget, devicePlatform, onDevicePlatformChange, unknownIncluded }: Props) {
  const isHybrid = devicePlatform.filter(d => d.enable).length > 1;
  const [selectedPlatform, setSelectedPlatform] = useState<PlatformTypes>(() =>
    isHybrid ? 'hybrid' : (devicePlatform.find(d => d.enable)?.name as PlatformTypes)
  );
  const [selectedDistributionType, setSelectedDistributionType] = useState<PlatformDistributionType>(() =>
    isHybrid && devicePlatform.some(d => d.distribution > 0) ? 'specific' : 'all'
  );
  const [selectedDistribution, setSelectedDistribution] = useState<number>(
    isHybrid ? devicePlatform.find(d => d.name === 'desktop')?.distribution || 0 : 100
  );
  const [currentUnknownIncluded, setCurrentUnknownIncluded] = useState<boolean>(unknownIncluded);

  const userEntity = useGlobalStore(getUser);
  const userRoles = useGlobalStore(getUserRoles);
  const user = userEntity.toJSON();
  const { formatMessage } = useIntl();

  const handlePlatformChange = (platform: PlatformTypes) => {
    setSelectedPlatform(platform);
    const newDevicePlatform = calculateDevicePlatform(platform, selectedDistributionType, selectedDistribution);
    onDevicePlatformChange(newDevicePlatform, currentUnknownIncluded);
  };

  const handleDistributionTypeChange = (distributionType: PlatformDistributionType) => {
    setSelectedDistributionType(distributionType);
    const distribution = distributionType === 'specific' ? 50 : selectedDistribution;
    setSelectedDistribution(distribution);
    const newDevicePlatform = calculateDevicePlatform(selectedPlatform, distributionType, distribution);
    onDevicePlatformChange(newDevicePlatform, currentUnknownIncluded);
  };

  const handleDistributionChange = (distribution: number) => {
    setSelectedDistribution(distribution);
    const newDevicePlatform = calculateDevicePlatform(selectedPlatform, selectedDistributionType, distribution);
    onDevicePlatformChange(newDevicePlatform, currentUnknownIncluded);
  };

  const handleUnknownIncludedChange = (_: any, checked: boolean) => {
    setCurrentUnknownIncluded(checked);
    onDevicePlatformChange(devicePlatform, checked);
  };

  const permittedPlatforms = Object.keys(DevicePlatformEnum).filter(platform =>
    checkUserResourcePermissions(`${platform}DevicePlatform`, user, userRoles)
  );

  const platformOptions = permittedPlatforms
    .filter(platform => platform !== DevicePlatformEnum.ctv)
    .map(platform => ({
      id: platform,
      text: formatMessage({
        defaultMessage: platform.charAt(0).toUpperCase() + platform.slice(1),
        id: `advertisingPlatform.options.${platform}`
      })
    }));

  const hybridOption = {
    id: 'hybrid',
    text: formatMessage({
      defaultMessage: 'Hybrid (Mobile & Desktop)',
      id: 'advertisingPlatform.options.hybrid'
    })
  };

  const radioButtonsGroup = [...platformOptions];
  const middleIndex = Math.ceil(radioButtonsGroup.length / 2);
  radioButtonsGroup.splice(middleIndex, 0, hybridOption);

  const showUnknownIncluded = checkUserResourcePermissions('unknownIncluded', user, userRoles);

  return (
    <AdvertisingPlatformStyled className="advertisingPlatform" id={'advertisingPlatform__box'}>
      <header className="advertisingPlatform__header">
        <div className="advertisingPlatform__title">
          {formatMessage({
            defaultMessage: 'Advertising Platform',
            id: 'advertisingPlatform.title'
          })}
        </div>
        <div className="advertisingPlatform__subtitle">
          {formatMessage({
            defaultMessage: 'Choose the type of platform for your campaign',
            id: 'advertisingPlatform.subtitle'
          })}
        </div>
      </header>
      <div className="advertisingPlatform__body">
        <div className="advertisingPlatform__radios">
          <RadioButtonsGroup
            className="advertisingPlatform__radiosItem"
            id={'advertisingPlatform__radios'}
            items={radioButtonsGroup}
            onChange={(newValue: string) => handlePlatformChange(newValue as PlatformTypes)}
            value={selectedPlatform}
          />
          {showUnknownIncluded ? (
            <Checkbox
              active={currentUnknownIncluded}
              id={'advertisingPlatform__unknownIncluded'}
              labelText={formatMessage({
                defaultMessage: 'Target Unknown',
                id: 'advertisingPlatform.unknownIncluded'
              })}
              onClickAction={handleUnknownIncludedChange}
              type="checkbox"
            />
          ) : null}
        </div>
        {selectedPlatform === 'hybrid' ? (
          <PlatformDistributionStyled className="platformDistribution" id={'advertisingPlatform__box'}>
            <div className="platformDistribution__title">
              {formatMessage({
                defaultMessage: 'Set platform distribution',
                id: 'advertisingPlatform.distribution.title'
              })}
            </div>
            <div>
              <div className="platformDistribution__body" key={selectedDistributionType}>
                <SelectionableSection
                  id={'platformDistribution__all'}
                  isSectionEnabled={selectedDistributionType === 'all'}
                  label={formatMessage({
                    defaultMessage: 'Target inventory for all incoming traffic.',
                    id: 'advertisingPlatform.distribution.all'
                  })}
                  onSectionEnabledChange={() => handleDistributionTypeChange('all')}
                />
                <SelectionableSection
                  id={'platformDistribution__specific'}
                  isSectionEnabled={selectedDistributionType === 'specific'}
                  label={formatMessage({
                    defaultMessage: `Target inventory for a specific distribution of incoming traffic. 
                This will increase inventory reach.`,
                    id: 'advertisingPlatform.distribution.specific'
                  })}
                  onSectionEnabledChange={() => handleDistributionTypeChange('specific')}
                />
                {selectedDistributionType === 'specific' ? (
                  <DistributionSlider
                    budget={budget}
                    className="advertisingPlatform__range"
                    id="advertisingPlatform__range"
                    leftLabel={formatMessage({
                      defaultMessage: 'Mobile',
                      id: 'advertisingPlatform.options.mobile'
                    })}
                    onChange={handleDistributionChange}
                    rightLabel={formatMessage({
                      defaultMessage: 'Desktop',
                      id: 'advertisingPlatform.options.desktop'
                    })}
                    title={formatMessage({
                      defaultMessage: 'Distribution percentage between platforms',
                      id: 'advertisingPlatform.distribution.range.title'
                    })}
                    value={selectedDistribution}
                  />
                ) : null}
              </div>
            </div>
          </PlatformDistributionStyled>
        ) : null}
      </div>
    </AdvertisingPlatformStyled>
  );
}
